import { lazyPage } from './lib/lazyPage';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ClientGate from './components/ClientGate';
import LinkStore from './pages/returns/LinkStore';
import ErrorBoundary from './components/ErrorBoundary';
import { loginPathFor, safeReturnTo } from './lib/returnTo';

function StagingBanner() {
  const { t } = useTranslation();

  if (import.meta.env.VITE_ENVIRONMENT !== 'staging') {
    return null;
  }

  return (
    <div className="bg-warning-400 text-white text-center text-sm py-1 font-medium">
      {t('staging.banner')}
    </div>
  );
}

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
const NewShipment = lazyPage(() => import('./pages/NewShipment'));
const Shipments = lazyPage(() => import('./pages/Shipments'));
const ShipmentDetails = lazyPage(() => import('./pages/ShipmentDetails'));
const Addresses = lazyPage(() => import('./pages/Addresses'));
const Profile = lazyPage(() => import('./pages/Profile'));
const AdminUsers = lazyPage(() => import('./pages/admin/AdminUsers'));
const AdminUserForm = lazyPage(() => import('./pages/admin/AdminUserForm'));
const AdminClients = lazyPage(() => import('./pages/admin/AdminClients'));
const AdminClientDetail = lazyPage(() => import('./pages/admin/AdminClientDetail'));
const AdminAddresses = lazyPage(() => import('./pages/admin/AdminAddresses'));
const Reports = lazyPage(() => import('./pages/Reports'));
import LoadingSpinner from './components/LoadingSpinner';

// RMA Pages
const RmaList = lazyPage(() => import('./pages/rma/RmaList'));
const RmaCreate = lazyPage(() => import('./pages/rma/RmaCreate'));
const RmaDetail = lazyPage(() => import('./pages/rma/RmaDetail'));
const RmaReceive = lazyPage(() => import('./pages/rma/RmaReceive'));
const AdminRmaList = lazyPage(() => import('./pages/rma/admin/AdminRmaList'));

// Claims Pages
const ClaimsList = lazyPage(() => import('./pages/claims/ClaimsList'));
const ClaimCreate = lazyPage(() => import('./pages/claims/ClaimCreate'));
const ClaimDetail = lazyPage(() => import('./pages/claims/ClaimDetail'));
const AdminClaimsList = lazyPage(() => import('./pages/claims/admin/AdminClaimsList'));
const AdminClaimDetail = lazyPage(() => import('./pages/claims/admin/AdminClaimDetail'));
const AdminCarrierContacts = lazyPage(() => import('./pages/admin/AdminCarrierContacts'));

// Replenishment Pages

const ReplenishmentReport = lazyPage(() => import('./pages/replenishment/ReplenishmentReport'));
const ReplenishmentProducts = lazyPage(() => import('./pages/replenishment/ReplenishmentProducts'));
const ReplenishmentGroups = lazyPage(() => import('./pages/replenishment/ReplenishmentGroups'));
const ReplenishmentSuppliers = lazyPage(() => import('./pages/replenishment/ReplenishmentSuppliers'));
const ReplenishmentPurchaseOrders = lazyPage(() => import('./pages/replenishment/ReplenishmentPurchaseOrders'));
const ReplenishmentPurchaseOrderDetail = lazyPage(() => import('./pages/replenishment/ReplenishmentPurchaseOrderDetail'));
const PurchaseOrderCreate = lazyPage(() => import('./pages/replenishment/PurchaseOrderCreate'));
const PurchaseOrderImport = lazyPage(() => import('./pages/replenishment/PurchaseOrderImport'));

// Products Pages
const ProductsList = lazyPage(() => import('./pages/products/ProductsList'));

// Orders Pages
const OrdersList = lazyPage(() => import('./pages/orders/OrdersList'));
const OrdersImport = lazyPage(() => import('./pages/orders/OrdersImport'));
const OrderCreate = lazyPage(() => import('./pages/orders/OrderCreate'));

// Warehousing
const WarehousingPage = lazyPage(() => import('./pages/warehousing/WarehousingPage'));
// Reception (client-facing LIVE receiving)
const ReceptionPage = lazyPage(() => import('./pages/reception/ReceptionPage'));
// Return decisions (Retourneur — held returns waiting on the client)
const ReturnDecisionsList = lazyPage(() => import('./pages/returns/ReturnDecisionsList'));
const ReturnDecisionDetail = lazyPage(() => import('./pages/returns/ReturnDecisionDetail'));

// Dashboard
const Dashboard = lazyPage(() => import('./pages/dashboard/Dashboard'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    // Keep where the user was going (e.g. an emailed return-decision link) so
    // login brings them back; the target is validated again on the way out.
    return <Navigate to={loginPathFor(location.pathname, location.search)} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { search } = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={safeReturnTo(new URLSearchParams(search).get('next')) ?? '/'} replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/**
 * Legacy /replenishment/purchase-orders/* → /procurement/purchase-orders/*.
 * Preserves the trailing segment (:id, /new, /import) and any query string so
 * old bookmarks and links already pasted into client emails still resolve.
 */
function RedirectToProcurementPO() {
  const params = useParams();
  const { search, hash } = useLocation();
  const rest = params['*'] ?? '';
  return <Navigate to={`/procurement/purchase-orders${rest ? `/${rest}` : ''}${search}${hash}`} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <Toaster position="top-right" richColors mobileOffset={{ top: 76 }} />
    <StagingBanner />
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<ClientGate module="orders"><Dashboard /></ClientGate>} />

        {/* Shipping module routes */}
        <Route path="new-shipment" element={<ClientGate><NewShipment /></ClientGate>} />
        <Route path="shipments" element={<ClientGate><Shipments /></ClientGate>} />
        <Route path="shipments/:id" element={<ShipmentDetails />} />
        <Route path="addresses" element={<Addresses />} />
        <Route path="profile" element={<Profile />} />
        <Route path="reports" element={<Reports />} />

        {/* RMA module routes */}
        <Route path="rma" element={<RmaList />} />
        <Route path="rma/new" element={<ClientGate module="rma"><RmaCreate /></ClientGate>} />
        <Route path="rma/receive" element={<RmaReceive />} />
        <Route path="rma/:id" element={<RmaDetail />} />

        {/* Return decisions — no module gate: any client's returns can wait on them.
            /retours/decisions/:id is the deep link in the decision email. */}
        <Route path="retours/decisions" element={<ClientGate><ReturnDecisionsList /></ClientGate>} />
        <Route path="retours/decisions/:id" element={<LinkStore><ClientGate><ReturnDecisionDetail /></ClientGate></LinkStore>} />

        {/* Claims module routes */}
        <Route path="claims" element={<ClaimsList />} />
        <Route path="claims/new" element={<ClientGate><ClaimCreate /></ClientGate>} />
        <Route path="claims/:id" element={<ClaimDetail />} />

        {/* Procurement module routes (standalone PO + Suppliers) */}
        <Route path="procurement" element={<Navigate to="/procurement/purchase-orders" replace />} />
        <Route path="procurement/purchase-orders" element={<ClientGate><ReplenishmentPurchaseOrders basePath="/procurement" /></ClientGate>} />
        <Route path="procurement/purchase-orders/new" element={<ClientGate><PurchaseOrderCreate basePath="/procurement" /></ClientGate>} />
        <Route path="procurement/purchase-orders/import" element={<ClientGate><PurchaseOrderImport basePath="/procurement" /></ClientGate>} />
        <Route path="procurement/purchase-orders/:id" element={<ClientGate><ReplenishmentPurchaseOrderDetail basePath="/procurement" /></ClientGate>} />
        <Route path="procurement/suppliers" element={<ClientGate><ReplenishmentSuppliers /></ClientGate>} />

        {/* Replenishment module routes (wrapped in shared client context) */}
        <Route path="replenishment" element={<Navigate to="/replenishment/report" replace />} />
        <Route path="replenishment/report" element={<ClientGate module="replenishment"><ReplenishmentReport /></ClientGate>} />
        <Route path="replenishment/products" element={<ClientGate module="replenishment"><ReplenishmentProducts /></ClientGate>} />
        <Route path="replenishment/groups" element={<ClientGate module="replenishment"><ReplenishmentGroups /></ClientGate>} />
        {/* Purchase orders have ONE home: /procurement (always-on module). This
            mount was a second front door to the same components — it gated the
            same PO behind the replenishment flag and made pasted URLs dead-end
            for clients without it. Redirect rather than delete so existing
            bookmarks and links already sent to clients keep working. */}
        <Route path="replenishment/purchase-orders/*" element={<RedirectToProcurementPO />} />

        {/* Products module routes */}
        <Route path="products" element={<ClientGate module="products"><ProductsList /></ClientGate>} />

        {/* Warehousing */}
        <Route path="warehousing" element={<ClientGate module="orders"><WarehousingPage /></ClientGate>} />

        {/* Reception (client-facing LIVE receiving) */}
        <Route path="reception" element={<ClientGate module="orders"><ReceptionPage /></ClientGate>} />

        {/* Orders module routes */}
        <Route path="orders" element={<ClientGate module="orders"><OrdersList /></ClientGate>} />
        <Route path="orders/new" element={<ClientGate module="orders"><OrderCreate /></ClientGate>} />
        <Route path="orders/import" element={<ClientGate module="orders"><OrdersImport /></ClientGate>} />

        {/* Admin routes */}
        <Route
          path="admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />
        <Route
          path="admin/users/:id"
          element={
            <AdminRoute>
              <AdminUserForm />
            </AdminRoute>
          }
        />
        <Route
          path="admin/clients"
          element={
            <AdminRoute>
              <AdminClients />
            </AdminRoute>
          }
        />
        <Route
          path="admin/clients/:id"
          element={
            <AdminRoute>
              <AdminClientDetail />
            </AdminRoute>
          }
        />
        <Route
          path="admin/addresses"
          element={
            <AdminRoute>
              <AdminAddresses />
            </AdminRoute>
          }
        />
        {/* Admin RMA route */}
        <Route
          path="admin/rma"
          element={
            <AdminRoute>
              <AdminRmaList />
            </AdminRoute>
          }
        />
        {/* Admin Claims routes */}
        <Route
          path="admin/claims"
          element={
            <AdminRoute>
              <AdminClaimsList />
            </AdminRoute>
          }
        />
        <Route
          path="admin/claims/new"
          element={
            <AdminRoute>
              <ClaimCreate adminMode />
            </AdminRoute>
          }
        />
        <Route
          path="admin/claims/:id"
          element={
            <AdminRoute>
              <AdminClaimDetail />
            </AdminRoute>
          }
        />
        <Route
          path="admin/carrier-contacts"
          element={
            <AdminRoute>
              <AdminCarrierContacts />
            </AdminRoute>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}
